import "../../App.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "react-notifications/lib/notifications.css";
import { NotificationContainer } from "react-notifications";
import SkyLoader from "../../components/sky-loader";
import { Navigate } from "react-router-dom"; // Import Navigate
import { useLoginLogic } from "../../hooks/useLoginLogic"; // Import the hook
import { useState, useEffect } from "react";

// Custom hook to detect screen width (Keep this here)
const useIsMobile = (breakpoint = 640) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, [breakpoint]);
  return isMobile;
};

const Login = () => {
  const isMobile = useIsMobile(980); // Adjusted breakpoint for desktop check

  // Use our new Logic Hook
  const {
    formData,
    handleInputChange,
    handleSubmit,
    isLoading,
    isDisabled,
    showPassword,
    togglePasswordVisibility,
    captcha,
    captchaInput,
    setCaptchaInput,
    generateCaptchaCode,
    validationError,
    captchaValidationError,
  } = useLoginLogic();

  // 1. If NOT mobile, redirect to Home (where Header login is)
  if (!isMobile) {
    return <Navigate to="/" replace />;
  }

  // 2. Render ONLY the Mobile Layout
  return (
    <>
      <NotificationContainer />
      {isLoading && <SkyLoader />}

      <div className="absolute w-full left-0 top-0 absolute z-[99999]">
        {/* MOBILE LAYOUT START */}
        <div className="w-full left-0 top-0 flex justify-center">
          <div className="relative w-full flex flex-col items-center h-screen overflow-auto z-[999] bg-[#ffb600]">
            {/* Top Header/Logo Section */}
            <div
              className="relative w-full h-[280px] flex flex-col items-center justify-center pt-10"
              style={{
                background: "url(/images/KV-pic.png) no-repeat center bottom",
                backgroundSize: "cover",
              }}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                <img
                  src="/logos/logo.png"
                  alt="Skyexch Logo"
                  className="w-24 h-24"
                />
              </div>
            </div>

            {/* Form Container Section */}
            <div className="w-full relative -mt-[50px] px-2 flex items-center justify-center">
              <div className="max-w-[80%] absolute top-[100px] w-full">
                <form
                  method="post"
                  onSubmit={handleSubmit}
                  autoComplete="off"
                  noValidate
                  className="w-full"
                >
                  {validationError && (
                    <div className="mb-3 text-sm text-red-700 bg-white/50 p-1 rounded text-center font-bold">
                      {validationError}
                    </div>
                  )}

                  <div className="mb-3">
                    <input
                      value={formData.user_name}
                      onChange={handleInputChange}
                      type="text"
                      name="user_name"
                      placeholder="Username"
                      className="w-full p-1 h-[37px] rounded-md
                        border-2 border-transparent
                        focus:outline-none
                        focus:border-none
                        focus:bg-[#fff0ca]
                        focus:text-[#1e1e1e]
                        focus:shadow-[inset_0_0.2666666667vw_1.3333333333vw_rgba(161,128,45,0.6)]
                        placeholder-gray-400 text-sx pl-3
                        "
                    />
                  </div>

                  <div className="mb-3">
                    <div className="relative">
                      <input
                        value={formData.password}
                        onChange={handleInputChange}
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Password"
                        className="w-full p-1 h-[37px] rounded-md
                            border-2 border-transparent
                            focus:outline-none
                            focus:border-none
                            focus:bg-[#fff0ca]
                            focus:text-[#1e1e1e]
                            focus:shadow-[inset_0_0.2666666667vw_1.3333333333vw_rgba(161,128,45,0.6)]
                            placeholder-gray-400 text-sx pl-3
                            "
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-black text-[18px]"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  {/* Mobile Captcha */}
                  <div className="relative w-full mb-3">
                    <input
                      value={captchaInput}
                      onChange={(e) => setCaptchaInput(e.target.value)}
                      type="text"
                      maxLength={4}
                      placeholder="Validation Code"
                      className="
                            w-full p-1 h-[37px] pr-28 rounded-md
                            border-2 border-transparent
                            focus:outline-none
                            focus:border-none
                            focus:bg-[#fff0ca]
                            focus:text-[#1e1e1e]
                            focus:shadow-[inset_0_0.2666666667vw_1.3333333333vw_rgba(161,128,45,0.6)]
                            placeholder-gray-400 text-sx pl-3
                        "
                    />

                    <span
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 text-black font-bold text-xl tracking-widest select-none bg-none rounded-md px-3 cursor-pointer"
                      onClick={generateCaptchaCode}
                    >
                      {captcha}
                    </span>
                  </div>
                  {captchaValidationError && (
                    <span className="text-red-700 font-bold text-sm mb-3 block text-center bg-white/50 rounded">
                      {captchaValidationError}
                    </span>
                  )}

                  <div className="mb-3">
                    <button
                      type="submit"
                      disabled={isDisabled}
                      className="w-full h-[40px] rounded-md text-sm font-bold text-[#ffb600] bg-gradient-to-t from-[#070707] to-[#474747] shadow-lg"
                    >
                      Login
                    </button>
                  </div>
                </form>

                {/* Mobile Footer Links */}
                <div className="flex flex-col gap-3">
                  <div className="w-full h-11 bg-[rgba(255,255,255,.6)] rounded-md flex items-center justify-center">
                    <span className="text-[rgba(0,0,0,.7)] text-base font-medium">
                      Customer support
                    </span>
                  </div>
                </div>
                <div className="h-20"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default Login;
